"use client"

import type React from "react"

import { useState } from "react"
import styles from "./ExistingTreatmentCard.module.css"
import { FiSettings, FiInfo, FiX, FiPlus } from "react-icons/fi"

import { useFeatureFlipStore } from "Store/featureFlipStore"
import ConfigModal from "../ConfigModal/ConfigModal"
import type { Treatment } from "API/Queries/useGetAllTreatments"
import ViewUseAdditionModal from "../ViewUserAdditionModal/ViewUserAdditionModal"
import { useGetAllContexts } from "API/Queries/useGetAllContexts"
import { useParams } from "react-router-dom"
import ViewSegmentAdditionModal from "../ViewSegmentAdditionModal/ViewSegmentAdditionModal"

interface IExistingTreatmentCardProps {
  treatment: Treatment
  index: number
  handleTreatmentChange: (
    index: number,
    field: string,
    value:
      | string
      | Record<string, string>
      | null
      | {
          new: string[]
          deleted: string[]
          existing: string[]
        },
  ) => void
  handleRemoveTreatment: (index: number) => void
}

const ExistingTreatmentCard = (props: IExistingTreatmentCardProps) => {
  const { treatment, index, handleTreatmentChange, handleRemoveTreatment } = props

  const { id: projectId } = useParams()
  const { duplicateErrors, setContexts, colors } = useFeatureFlipStore()
  const [showConfigModal, setShowConfigModal] = useState(false)
  const [showUserModal, setShowUserModal] = useState(false)
  const [showSegmentModal, setShowSegmentModal] = useState(false)
  const { mutateAsync: getAllContexts } = useGetAllContexts()

  // Extract complex logic
  const isDefaultTreatment = treatment.name.toLowerCase() === "on" || treatment.name.toLowerCase() === "off"

  const configCount = treatment.configuration ? Object.keys(treatment.configuration).length : 0

  const hasDuplicateError = duplicateErrors[index]
  const inputClassName = `${styles.treatmentInput} ${hasDuplicateError ? styles.error : ""}`
  const emptyDescriptionClassName = `${styles.treatmentInput} ${!treatment.description ? styles.emptyDescription : ""}`
  const isAddUserDisabled = !treatment.name
  const isAddSegmentDisabled = !treatment.name

  // Extract event handlers
  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleTreatmentChange(index, "name", e.target.value)
  }

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleTreatmentChange(index, "description", e.target.value || null)
  }

  const handleConfigModalOpen = () => {
    setShowConfigModal(true)
  }

  const handleConfigModalClose = () => {
    setShowConfigModal(false)
  }

  const handleConfigChange = (updatedConfig: Record<string, string>) => {
    handleTreatmentChange(index, "configuration", updatedConfig)
  }

  const handleUserModalOpen = async () => {
    await getAllContexts(projectId as string, {
      onSuccess: (data) => {
        setContexts(data)
      },
    })
    setShowUserModal(true)
  }

  const handleUserModalClose = () => {
    setShowUserModal(false)
  }

  const handleSegmentModalOpen = () => {
    setShowSegmentModal(true)
  }

  const handleSegmentModalClose = () => {
    setShowSegmentModal(false)
  }

  const handleRemoveClick = () => {
    handleRemoveTreatment(index)
  }

  const getTreatmentColor = () => {
    if (treatment.name.toLowerCase() === "on") return "#4945FF"
    if (treatment.name.toLowerCase() === "off") return "#F87171"
    return colors[index]
  }

  const getConfigTooltipText = () => {
    return `${configCount} configuration${configCount !== 1 ? "s" : ""}`
  }

  // Render info tooltip for default treatments
  if (isDefaultTreatment) {
    return (
      <>
        <div className={styles.treatmentItem}>
          <div className={styles.treatmentItemContent}>
            <div className={styles.treatmentColor} style={{ background: getTreatmentColor() }} />
            <div className={styles.inputContainer}>
              <input
                type="text"
                value={treatment.name}
                onChange={handleNameChange}
                placeholder="Edit treatment name"
                className={inputClassName}
                readOnly={isDefaultTreatment}
              />
            </div>
            <div className={styles.descriptionWrapper}>
              <input
                type="text"
                value={treatment.description || ""}
                onChange={handleDescriptionChange}
                placeholder="Add description"
                className={emptyDescriptionClassName}
              />
            </div>
            <div className={styles.buttonGroup}>
              <div className={styles.configButtonWrapper}>
                <button className={styles.configButton} onClick={handleConfigModalOpen}>
                  <FiSettings />
                </button>
                {configCount > 0 && (
                  <span className={styles.configBadge} title={getConfigTooltipText()}>
                    {configCount}
                  </span>
                )}
              </div>
              <button className={styles.addButton} onClick={handleUserModalOpen} disabled={isAddUserDisabled}>
                <FiPlus /> Add User
              </button>
              <button className={styles.addButton} onClick={handleSegmentModalOpen} disabled={isAddSegmentDisabled}>
                <FiPlus /> Add Segment
              </button>
              <div className={styles.infoTooltip}>
                <FiInfo className={styles.infoIcon} />
                <span className={styles.tooltipText}>Cannot delete default treatments</span>
              </div>
            </div>
          </div>
          {hasDuplicateError && <div className={styles.errorMessage}>{duplicateErrors[index]}</div>}
        </div>
        {showConfigModal && (
          <ConfigModal
            handleTreatmentChange={handleConfigChange}
            name={treatment.name}
            treatmentConfig={treatment.configuration}
            onClose={handleConfigModalClose}
          />
        )}
        {showUserModal && (
          <ViewUseAdditionModal
            selectedTreatmentId={treatment.id}
            selectedTreatmentName={treatment.name}
            setShowUserModal={setShowUserModal}
          />
        )}
        {showSegmentModal && (
          <ViewSegmentAdditionModal
            selectedTreatmentId={treatment.id}
            onClose={handleSegmentModalClose}
            selectedTreatmentName={treatment.name}
          />
        )}
      </>
    )
  }

  return (
    <>
      <div className={styles.treatmentItem}>
        <div className={styles.treatmentItemContent}>
          <div className={styles.treatmentColor} style={{ background: getTreatmentColor() }} />
          <div className={styles.inputContainer}>
            <input
              type="text"
              value={treatment.name}
              onChange={handleNameChange}
              placeholder="Edit treatment name"
              className={inputClassName}
              readOnly={isDefaultTreatment}
            />
          </div>
          <div className={styles.descriptionWrapper}>
            <input
              type="text"
              value={treatment.description || ""}
              onChange={handleDescriptionChange}
              placeholder="Add description"
              className={emptyDescriptionClassName}
            />
          </div>
          <div className={styles.buttonGroup}>
            <div className={styles.configButtonWrapper}>
              <button className={styles.configButton} onClick={handleConfigModalOpen}>
                <FiSettings />
              </button>
              {configCount > 0 && (
                <span className={styles.configBadge} title={getConfigTooltipText()}>
                  {configCount}
                </span>
              )}
            </div>
            <button className={styles.addButton} onClick={handleUserModalOpen} disabled={isAddUserDisabled}>
              <FiPlus /> Add User
            </button>
            <button className={styles.addButton} onClick={handleSegmentModalOpen} disabled={isAddSegmentDisabled}>
              <FiPlus /> Add Segment
            </button>
            <button className={styles.removeButton} onClick={handleRemoveClick}>
              <FiX />
            </button>
          </div>
        </div>
        {hasDuplicateError && <div className={styles.errorMessage}>{duplicateErrors[index]}</div>}
      </div>
      {showConfigModal && (
        <ConfigModal
          handleTreatmentChange={handleConfigChange}
          name={treatment.name}
          treatmentConfig={treatment.configuration}
          onClose={handleConfigModalClose}
        />
      )}
      {showUserModal && (
        <ViewUseAdditionModal
          selectedTreatmentId={treatment.id}
          selectedTreatmentName={treatment.name}
          setShowUserModal={setShowUserModal}
        />
      )}
      {showSegmentModal && (
        <ViewSegmentAdditionModal
          selectedTreatmentId={treatment.id}
          onClose={handleSegmentModalClose}
          selectedTreatmentName={treatment.name}
        />
      )}
    </>
  )
}

export default ExistingTreatmentCard
